import {
  BadgeCell,
  HtmlCell,
  ImageCell,
  TextCell,
} from "@/components/table/CustomCell";
import { TypeDefaultImage } from "@/lib/image";
import { t as globalT, TFunction } from "i18next";

export interface ColumnFormatted<T = any, TContext = any> {
  type: string;
  key: string;
  visible?: boolean;
  formattedData?: (data: T, context?: TContext) => string;
}

export const getFormattedContent = <TContext = any,>(
  data: Record<string, unknown>,
  config?: ColumnFormatted[],
  col?: string,
  context?: TContext,
): string | undefined => {
  if (!config && (!col || !(col in data))) {
    return undefined;
  }

  if (!config && col && col in data) return String(data[col]);

  if (!config || !col) return undefined;

  const relatedConfig = config.find((c) => c.key === col);

  if (!relatedConfig) {
    return undefined;
  }

  if (!relatedConfig?.formattedData) return String(data[col]);

  return relatedConfig?.formattedData(data, context);
};

export const generateTable = <TContext = any,>(
  columns: ColumnFormatted[],
  domain: TypeDefaultImage,
  localT?: TFunction,
  context?: TContext,
) => {
  const t = localT ?? globalT;
  return columns.map((column: ColumnFormatted) => ({
    accessorKey: column.key,
    header: column.type !== "image" ? t(`tab_${column.key}`) : "",
    cell: (props: any) => {
      if (column.type === "image") {
        return (
          <ImageCell
            value={props.getValue()}
            attachments={props.row.original.attachments}
            domain={domain}
          />
        );
      } else if (column.type === "boolean") {
        return (
          <BadgeCell
            value={props.getValue() ? t("oui") : t("non")}
            variant={props.getValue() ? "published" : "draft"}
          />
        );
      } else if (column.type === "status") {
        return (
          <BadgeCell
            value={
              props.getValue() === "published" ? t("published") : t("draft")
            }
            variant={props.getValue() === "published" ? "published" : "draft"}
          />
        );
      } else if (column.type === "richtext") {
        return <HtmlCell value={props.getValue()} />;
      } else if (column.type === "string") {
        return <TextCell value={props.getValue()} />;
      } else if (column.type === "computed") {
        const formattedData = column.formattedData
          ? column.formattedData(props.row.original, context)
          : null;
        return <HtmlCell value={formattedData ?? ""} />;
      } else if (column.type === "badge") {
        const formattedData = column.formattedData
          ? column.formattedData(props.row.original, context)
          : props.getValue();
        return <BadgeCell value={formattedData} variant="outlined" />;
      } else {
        return;
      }
    },
  }));
};
